import { useLang } from '../i18n/lang';
import type { MockRoom } from './v8/mocks';

/*
 * WHO ELSE IS HERE — the top-right cluster, and the bottom-left rooms.
 *
 * The reference's map reads as INHABITED and ours reads as a diagram, and this is most of the
 * difference that is not the canvas: a count of who is exploring, and the rooms with a live voice
 * in them. None of it is scripture and none of it is drawn; it is the social plane, sitting in the
 * corners where it does not cover the map.
 *
 * IT ALSO CARRIED A RUNNING FEED of what people were doing, and that went on 2026-08-22 — see the
 * note beside where it was drawn, below.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * EVERYTHING THIS SHOWS IS INVENTED TODAY — `map/v8/mocks.ts`, behind `?mock=1`.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The component is real and the data is not. Each piece names the source that has to replace it:
 * the count is a sum over `usePresence`, and the rooms are `topRooms` over the rooms service. It
 * takes them as props precisely so the swap is a change of caller rather than a rewrite of this.
 */

export interface MapPresenceClusterProps {
  /** How many people are reading, anywhere. Null hides the count rather than showing a zero. */
  exploring: number | null;
  rooms: readonly MockRoom[];
  /** Whether what is shown is invented. Drawn on screen, not just commented. */
  invented: boolean;
  /**
   * WHAT SITS BESIDE THE COUNT — the reader's own control, and theirs is in exactly this place.
   *
   * A slot rather than an import, because this component knows about everybody else and has no
   * business knowing about the reader. It was a fixed corner of its own first, and floated straight
   * over this cluster's feed: "reader_27 left a note on" with a circle through the middle of it.
   * Two things pinned to the same corner by two different rules is how that happens.
   */
  beside?: React.ReactNode;
}

export function MapPresenceCluster({ exploring, rooms, invented, beside }: MapPresenceClusterProps): React.JSX.Element | null {
  const { t } = useLang();
  if (exploring === null && rooms.length === 0) return null;

  return (
    <>
      <div className="presence-cluster" data-map-chrome>
        {/* ONE ROW, count then control — theirs reads "1,425 exploring" and then a round YOU, side
            by side in the bar. Stacked, ours put the reader above everybody else, which is neither
            theirs nor a sentence. */}
        <div className="presence-top">
        {exploring !== null && (
          <p className="presence-count">
            {/*
              * THREE FACES, WHICH ARE AN ICON AND NOT PEOPLE.
              *
              * Theirs opens its count with three overlapping circles, and they stand for "people"
              * the way the magnifier in the search stands for "look" — no reader is behind any of
              * them and none is claimed to be. Hidden from assistive technology for exactly that
              * reason: the sentence beside them already says how many, and a screen reader
              * announcing three anonymous shapes would be inventing three readers.
              *
              * They must stay decoration. The moment the presence plane can say WHO is here, the
              * map still may not: being in a public room is consent to be in that room, not to be
              * plotted — see the note at the head of this file.
              */}
            <span className="presence-faces" aria-hidden="true">
              <i /><i /><i />
            </span>
            {t('feed.exploring', { count: exploring.toLocaleString() })}{' '}
            {/*
              * SAID ON SCREEN, not only in a comment. A reader looking at a screenshot of this has
              * no other way to know the people in it are not real, and a mock that cannot be told
              * from the real thing is the one that ends up in a slide.
              */}
            {invented && <span className="presence-mock" title="mock data">mock</span>}{' '}
          </p>
        )}
        {beside !== undefined && <div className="presence-beside">{beside}</div>}
        </div>
        {/*
          * THE FEED IS GONE — dropped on Ariel's instruction, 2026-08-22: *"drop the feature that
          * shows a popup under the search showing 'reader x' with some text underneath"*.
          *
          * It listed four invented lines of `reader_27 left a note on <figure>` here. Removed whole
          * rather than left as a prop nothing renders, because a component that still takes a feed
          * and draws none is the "ported, covered and unreachable" shape this repository keeps
          * paying for. The reasoning it carried — that whether a REAL feed may name anybody is an
          * open decision, and the presence plane deliberately carries counts and never names — is
          * kept in `v8/mocks.ts` where the generator was.
          */}
      </div>

      {rooms.length > 0 && (
        <div className="presence-rooms" data-map-chrome>
          <p className="presence-rooms-title">
            {/* THE SPACES ARE LOAD-BEARING, all of them. Sibling elements concatenate in
                `textContent` with nothing between them, so this corner read back as
                "1,200 exploringLive voicePersia23Huldah36" where theirs reads
                "LIVE VOICE Elohim 30 Adam 31". A number welded to a name is a different number.
                Every leaf that can abut another ends in one, rather than each join being patched —
                patching joins is how the first attempt fixed "Persia23" and left "23Huldah". */}
            {t('feed.voice')}{' '}
            {invented && <span className="presence-mock" title="mock data">mock</span>}{' '}
          </p>
          <ul>
            {rooms.map(room => (
              <li key={room.name}>
                {/* Same again, and worse here because both halves are data: this read back as
                    "Persia23Huldah36" where theirs reads "Elohim 30 Adam 31". A number welded to a
                    name is a different number. */}
                <span className="presence-name">{room.name}</span>{' '}
                <span className="presence-readers">{room.readers}</span>{' '}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
